import { z } from "zod";
import { CANONICAL_RESULT_SCHEMA_VERSION, canonicalResultJson, canonicalResultValueSchema, } from "./canonical-result.js";
import { certifiedCompactFactEnvelopeSchema, } from "./twin-execution-certificates.js";
export const TWIN_EXECUTION_RESULT_EVIDENCE_SCHEMA_VERSION = "mlx.execution-result-evidence.v1";
export const TWIN_EXECUTION_CSV_CANONICALIZATION_VERSION = "mlx.csv-artifact-bytes.v1";
const BOOLEAN_OID = 16;
const DATE_OID = 1082;
const TIMESTAMP_OIDS = new Set([1114, 1184]);
const resultColumnSchema = z.object({
    name: z.string().trim().min(1).max(240),
    postgresTypeOid: z.number().int().nonnegative(),
}).strict();
const resultRowSchema = z.record(z.string().trim().min(1).max(240), canonicalResultValueSchema);
/**
 * Typed result evidence carried next to the worker-authored certificate draft.
 * Inline governed queries carry every returned row. CSV exports carry at most
 * the compact-fact preview because the control plane hashes the persisted
 * artifact bytes independently instead of transporting the full export again.
 */
export const twinExecutionResultEvidenceV1Schema = z.object({
    schemaVersion: z.literal(TWIN_EXECUTION_RESULT_EVIDENCE_SCHEMA_VERSION),
    authority: z.enum(["inline_query_result", "persisted_csv_artifact"]),
    columns: z.array(resultColumnSchema).min(1).max(256),
    rows: z.array(resultRowSchema).max(200),
    rowCount: z.number().int().nonnegative().max(10_000),
    sourceComplete: z.boolean(),
    sourceRelations: z.array(z.string().trim().min(1).max(300)).min(1).max(16),
    completenessCaveat: z.string().trim().min(1).max(500).optional(),
    // A period bucket in the result contains today; rendered with every answer that cites the source.
    openPeriodCaveat: z.string().trim().min(1).max(500).optional(),
}).strict().superRefine((evidence, context) => {
    const columnNames = evidence.columns.map((column) => column.name);
    if (new Set(columnNames).size !== columnNames.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["columns"],
            message: "Execution result evidence columns must be unique.",
        });
    }
    if (new Set(evidence.sourceRelations).size !== evidence.sourceRelations.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["sourceRelations"],
            message: "Execution result evidence relations must be unique.",
        });
    }
    for (const [rowIndex, row] of evidence.rows.entries()) {
        const rowColumns = Object.keys(row);
        if (rowColumns.length !== columnNames.length ||
            columnNames.some((column) => !(column in row)) ||
            rowColumns.some((column) => !columnNames.includes(column))) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["rows", rowIndex],
                message: "Every execution evidence row must contain exactly the declared columns.",
            });
        }
    }
    if (evidence.authority === "inline_query_result" &&
        evidence.rowCount !== evidence.rows.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["rowCount"],
            message: "Inline result evidence must carry every returned row.",
        });
    }
    if (evidence.authority === "persisted_csv_artifact" &&
        (evidence.rows.length > 12 || evidence.rows.length > evidence.rowCount ||
            !evidence.sourceComplete)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["rows"],
            message: "CSV evidence must describe a complete export with at most 12 preview rows.",
        });
    }
    if (evidence.sourceComplete && evidence.completenessCaveat) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["completenessCaveat"],
            message: "Complete source evidence cannot carry an incompleteness caveat.",
        });
    }
    if (!evidence.sourceComplete && !evidence.completenessCaveat) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["completenessCaveat"],
            message: "Incomplete source evidence must explain its completeness boundary.",
        });
    }
    if (JSON.stringify(evidence).length > 1_000_000) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: [],
            message: "Execution result evidence exceeds the 1,000,000 character transport bound.",
        });
    }
});
function compareText(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
function stableJson(value) {
    if (Array.isArray(value))
        return `[${value.map(stableJson).join(",")}]`;
    if (value && typeof value === "object") {
        return `{${Object.entries(value)
            .sort(([left], [right]) => compareText(left, right))
            .map(([key, entry]) => `${JSON.stringify(key)}:${stableJson(entry)}`)
            .join(",")}}`;
    }
    return JSON.stringify(value) ?? JSON.stringify(String(value));
}
/** Exact canonical result bytes for a bounded inline warehouse result. */
export function twinExecutionInlineCanonicalResultJson(input) {
    const evidence = twinExecutionResultEvidenceV1Schema.parse(input);
    if (evidence.authority !== "inline_query_result") {
        throw new Error("execution_result_evidence_not_inline");
    }
    const columns = evidence.columns.map((column) => column.name);
    let rowKey = "__mlx_certificate_row_ordinal";
    while (columns.includes(rowKey))
        rowKey = `_${rowKey}`;
    const rows = evidence.rows
        .map((row) => Object.fromEntries(columns.map((column) => [column, row[column]])))
        .sort((left, right) => compareText(stableJson(left), stableJson(right)))
        .map((row, index) => ({
        ...row,
        [rowKey]: { kind: "integer", value: String(index) },
    }));
    return canonicalResultJson({
        schemaVersion: CANONICAL_RESULT_SCHEMA_VERSION,
        columns: [...columns, rowKey],
        stableKey: [rowKey],
        rows,
    });
}
function diversityKeyColumns(columns) {
    const exactYear = columns.filter((column) => /(?:^|_)(?:calendar_year|accounting_year|fiscal_year|year)$/iu.test(column));
    if (exactYear.length > 0)
        return exactYear.slice(0, 3);
    return columns.filter((column) => /(?:^|_)(?:period|month|date|due_month|accounting_month_date)$/iu.test(column))
        .slice(0, 3);
}
function selectedRows(evidence, maxRows) {
    if (evidence.rows.length <= maxRows)
        return evidence.rows;
    const selected = [];
    const selectedIndexes = new Set();
    const keys = diversityKeyColumns(evidence.columns.map((column) => column.name));
    if (keys.length > 0) {
        const seen = new Set();
        for (const [index, row] of evidence.rows.entries()) {
            const key = keys.map((column) => stableJson(row[column])).join("|");
            if (seen.has(key))
                continue;
            seen.add(key);
            selected.push(row);
            selectedIndexes.add(index);
            if (selected.length >= maxRows)
                return selected;
        }
    }
    for (const [index, row] of evidence.rows.entries()) {
        if (selectedIndexes.has(index))
            continue;
        selected.push(row);
        if (selected.length >= maxRows)
            return selected;
    }
    return selected;
}
/** Derive the certificate's compact facts solely from the bounded typed evidence. */
export function certifiedCompactFactEnvelopeFromResultEvidence(input) {
    const evidence = twinExecutionResultEvidenceV1Schema.parse(input);
    const allColumns = evidence.columns.map((column) => column.name);
    const selectedColumns = allColumns.slice(0, 24);
    const caveats = [];
    if (!evidence.sourceComplete)
        caveats.push(evidence.completenessCaveat);
    if (selectedColumns.length < allColumns.length) {
        caveats.push(`Fact values omit ${allColumns.length - selectedColumns.length} columns to stay within the compact envelope bound.`);
    }
    const facts = selectedRows(evidence, 12).map((row, rowIndex) => ({
        label: `Query result row ${rowIndex + 1}`,
        values: Object.fromEntries(selectedColumns.map((column) => [column, row[column]])),
    }));
    const compactComplete = evidence.sourceComplete && facts.length >= evidence.rowCount;
    if (facts.length < evidence.rowCount) {
        caveats.push(`Compact fact values include ${facts.length} of ${evidence.rowCount} rows; use a complete aggregate summary or certified artifact before making full-list claims.`);
    }
    const timeValues = evidence.rows.flatMap((row) => selectedColumns.flatMap((column) => {
        const value = row[column];
        return value.kind === "date" || value.kind === "timestamp"
            ? [{ column, value: value.value }]
            : [];
    }));
    const currencyCodes = [...new Set(facts.flatMap((fact) => Object.entries(fact.values)
            .flatMap(([column, value]) => /currency(?:_code)?$/iu.test(column) &&
            value.kind === "string" && /^[A-Z]{3}$/u.test(value.value) ? [value.value] : [])))
    ].sort().slice(0, 8);
    const units = selectedColumns.flatMap((column) => {
        const normalized = column.toLowerCase();
        const postgresTypeOid = evidence.columns.find((entry) => entry.name === column)
            .postgresTypeOid;
        const unit = /currency(?:_code)?$/u.test(normalized) ? "currency_code"
            : /(?:percent|percentage|pct)$/u.test(normalized) ? "percent"
                : /(?:^|_)count$/u.test(normalized) ? "count"
                    : /(?:^|_)days?$/u.test(normalized) ? "days"
                        : /(?:^|_)hours?$/u.test(normalized) ? "hours"
                            : postgresTypeOid === DATE_OID ? "date"
                                : TIMESTAMP_OIDS.has(postgresTypeOid) ? "timestamp"
                                    : postgresTypeOid === BOOLEAN_OID ? "boolean"
                                        : undefined;
        return unit ? [{ column, unit }] : [];
    });
    const envelope = {
        schemaVersion: "mlx.certified-compact-facts.v1",
        status: evidence.rowCount > 0
            ? "present"
            : compactComplete ? "absent" : "inconclusive",
        rowCount: evidence.rowCount,
        columnCount: allColumns.length,
        complete: compactComplete,
        zeroRowAssertion: evidence.rowCount === 0 && compactComplete,
        sourceRelations: evidence.sourceRelations,
        grain: {
            kind: "query_result_row",
            description: "Each fact is one row returned by the certified SQL result at the query's declared grouping grain.",
        },
        timeBoundary: timeValues.length > 0 ? {
            kind: "observed_values",
            start: [...timeValues].sort((left, right) => left.value.localeCompare(right.value))[0].value,
            end: [...timeValues].sort((left, right) => right.value.localeCompare(left.value))[0].value,
            columns: [...new Set(timeValues.map((entry) => entry.column))].slice(0, 8),
        } : { kind: "not_expressed", columns: [] },
        currencyCodes,
        units,
        facts,
        caveats,
    };
    const parsed = certifiedCompactFactEnvelopeSchema.safeParse(envelope);
    if (parsed.success)
        return parsed.data;
    return certifiedCompactFactEnvelopeSchema.parse({
        ...envelope,
        status: evidence.rowCount > 0 ? "present" : "inconclusive",
        complete: false,
        zeroRowAssertion: false,
        facts: [],
        caveats: [...caveats,
            "Typed fact values exceeded compact-envelope limits; use a smaller complete aggregate summary or certified artifact before making full-population claims."],
    });
}
